import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  increment,
  Timestamp,
  writeBatch,
  QueryConstraint,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  ObjectiveCreateSchema,
  ObjectiveUpdateSchema,
  type ObjectiveCreate,
  type ObjectiveUpdate,
} from '../models/objective.schemas';
import type { Objective } from '../models/objective.types';
import { keyResultService } from './keyResultService';
import { calculateObjectiveProgress } from '../utils/progressCalculation';

/**
 * Classe de serviço para operações Firestore relacionadas a Objetivos
 */
class ObjectiveServiceClass {
  private collectionName = 'objectives';
  private snapshotManager: {
    unsub: (() => void) | null;
    listeners: number;
  } = { unsub: null, listeners: 0 };

  /**
   * Cria um novo objetivo
   * @param data - Dados do objetivo a ser criado
   * @returns ID do objetivo criado
   * @throws Error se exceder limite de 4 objetivos por usuário por ciclo
   */
  async create(data: ObjectiveCreate): Promise<string> {
    // Validar com Zod schema
    const validated = ObjectiveCreateSchema.parse(data);

    // Validar limite de 4 objetivos ativos por usuário por ciclo
    const activeObjectivesQuery = query(
      collection(db, this.collectionName),
      where('cycleId', '==', validated.cycleId),
      where('ownerId', '==', validated.ownerId),
      where('status', 'in', ['draft', 'active'])
    );

    const activeObjectivesSnapshot = await getDocs(activeObjectivesQuery);
    if (activeObjectivesSnapshot.size >= 4) {
      throw new Error('okr/max-objectives-exceeded: Máximo de 4 objetivos por ciclo atingido');
    }

    // Validar que o pai existe e está no mesmo ciclo (se fornecido)
    if (validated.parentObjectiveId) {
      const parentDoc = await getDoc(
        doc(db, this.collectionName, validated.parentObjectiveId)
      );

      if (!parentDoc.exists()) {
        throw new Error('okr/invalid-parent: Objetivo pai não encontrado');
      }

      const parentData = parentDoc.data();
      if (parentData.cycleId !== validated.cycleId) {
        throw new Error('okr/invalid-parent: Objetivo pai deve estar no mesmo ciclo');
      }

      // Validar hierarquia de níveis
      if (parentData.level >= validated.level) {
        throw new Error(
          'okr/invalid-level-hierarchy: O nível do objetivo filho deve ser maior que o do pai'
        );
      }
    }

    // Criar documento
    const docRef = await addDoc(collection(db, this.collectionName), {
      ...validated,
      progress: 0,
      keyResultCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      completedAt: null,
      _metadata: {
        version: 1,
        lastModifiedBy: validated.ownerId,
      },
    });

    // Update path with actual document ID
    const actualPath = validated.parentObjectivePath
      ? `${validated.parentObjectivePath}/${docRef.id}`
      : `/${validated.cycleId}/${docRef.id}`;

    await updateDoc(docRef, {
      path: actualPath,
    });

    return docRef.id;
  }

  /**
   * Atualiza um objetivo existente
   * @param id - ID do objetivo
   * @param data - Dados a serem atualizados
   */
  async update(id: string, data: Partial<ObjectiveUpdate>): Promise<void> {
    // Validar com Zod schema (ObjectiveUpdateSchema já é partial)
    const validated = ObjectiveUpdateSchema.parse({ ...data, id });

    // Buscar objetivo atual para validações
    const objectiveDoc = await getDoc(doc(db, this.collectionName, id));
    if (!objectiveDoc.exists()) {
      throw new Error('okr/objective-not-found: Objetivo não encontrado');
    }

    const currentData = objectiveDoc.data();

    // Validar transições de status
    if (validated.status && currentData.status) {
      const invalidTransitions = ['completed', 'rolled', 'cancelled'];
      if (
        invalidTransitions.includes(currentData.status) &&
        validated.status !== currentData.status
      ) {
        throw new Error(
          'okr/invalid-status-transition: Não é possível alterar status de objetivos concluídos, transferidos ou cancelados'
        );
      }
    }

    // Preparar dados de atualização
    const updateData: Record<string, unknown> = {
      ...validated,
      updatedAt: serverTimestamp(),
      '_metadata.version': increment(1),
      '_metadata.lastModifiedBy': data._metadata?.lastModifiedBy || currentData.ownerId,
    };

    // Remover campos que não devem ser atualizados
    delete updateData.id;
    delete updateData._metadata;

    // Remover campos undefined (Firestore não aceita valores undefined)
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined) {
        delete updateData[key];
      }
    });

    // Atualizar documento
    await updateDoc(doc(db, this.collectionName, id), updateData);
  }

  /**
   * Arquiva um objetivo
   * @param id - ID do objetivo a ser arquivado
   * @param userId - ID do usuário que está arquivando
   * @throws Error se o objetivo tiver filhos ativos
   */
  async archive(id: string, userId: string): Promise<void> {
    // Verificar se existem filhos ativos
    const childrenQuery = query(
      collection(db, this.collectionName),
      where('parentObjectiveId', '==', id),
      where('status', 'in', ['draft', 'active'])
    );

    const childrenSnapshot = await getDocs(childrenQuery);
    if (childrenSnapshot.size > 0) {
      throw new Error(
        'okr/has-active-children: Não é possível arquivar objetivo com filhos ativos'
      );
    }

    // Arquivar objetivo
    await updateDoc(doc(db, this.collectionName, id), {
      status: 'cancelled',
      updatedAt: serverTimestamp(),
      '_metadata.version': increment(1),
      '_metadata.lastModifiedBy': userId,
    });
  }

  /**
   * Busca um objetivo por ID
   * @param id - ID do objetivo
   * @returns Objetivo encontrado
   */
  async getById(id: string): Promise<Objective> {
    const objectiveDoc = await getDoc(doc(db, this.collectionName, id));

    if (!objectiveDoc.exists()) {
      throw new Error('okr/objective-not-found: Objetivo não encontrado');
    }

    return this.formatObjective(objectiveDoc.id, objectiveDoc.data());
  }

  /**
   * Busca objetivos por ciclo com filtros opcionais
   * @param cycleId - ID do ciclo
   * @param filters - Filtros opcionais (userId, level, status)
   * @returns Lista de objetivos
   */
  async getByCycle(
    cycleId: string,
    filters?: {
      userId?: string;
      level?: number;
      status?: string;
    }
  ): Promise<Objective[]> {
    const constraints: QueryConstraint[] = [
      where('cycleId', '==', cycleId),
      where('status', '!=', 'cancelled'),
    ];

    if (filters?.userId) {
      constraints.push(where('ownerId', '==', filters.userId));
    }

    if (filters?.level) {
      constraints.push(where('level', '==', filters.level));
    }

    if (filters?.status) {
      constraints.push(where('status', '==', filters.status));
    }

    constraints.push(orderBy('status'), orderBy('createdAt', 'desc'));

    const objectivesQuery = query(collection(db, this.collectionName), ...constraints);
    const snapshot = await getDocs(objectivesQuery);

    return snapshot.docs.map((doc) => this.formatObjective(doc.id, doc.data()));
  }

  /**
   * Busca árvore de descendentes de um objetivo
   * @param parentId - ID do objetivo pai
   * @param cycleId - ID do ciclo
   * @returns Lista de objetivos descendentes
   */
  async getTree(parentId: string, cycleId: string): Promise<Objective[]> {
    // Buscar objetivo pai
    const parentDoc = await getDoc(doc(db, this.collectionName, parentId));
    if (!parentDoc.exists()) {
      throw new Error('okr/objective-not-found: Objetivo pai não encontrado');
    }

    // Buscar descendentes usando ancestorIds
    const descendantsQuery = query(
      collection(db, this.collectionName),
      where('cycleId', '==', cycleId),
      where('ancestorIds', 'array-contains', parentId),
      orderBy('level', 'asc')
    );

    const snapshot = await getDocs(descendantsQuery);
    return snapshot.docs.map((doc) => this.formatObjective(doc.id, doc.data()));
  }

  /**
   * Busca todos os descendentes de um objetivo (recursivamente)
   * @param objectiveId - ID do objetivo
   * @returns Lista de todos os descendentes
   */
  async getAllDescendants(objectiveId: string): Promise<Objective[]> {
    // Buscar objetivo para obter cycleId
    const objectiveDoc = await getDoc(doc(db, this.collectionName, objectiveId));
    if (!objectiveDoc.exists()) {
      throw new Error('okr/objective-not-found: Objetivo não encontrado');
    }

    const cycleId = objectiveDoc.data().cycleId;
    return this.getTree(objectiveId, cycleId);
  }

  /**
   * Inscreve-se para atualizações em tempo real de objetivos
   * @param cycleId - ID do ciclo
   * @param callback - Função de callback chamada quando há atualizações
   * @returns Função de cancelamento de inscrição
   */
  subscribeToObjectives(
    cycleId: string,
    callback: (objectives: Objective[]) => void
  ): () => void {
    console.log('[ObjectiveService] subscribeToObjectives called', {
      cycleId,
      collection: this.collectionName,
      timestamp: new Date().toISOString()
    });

    const objectivesQuery = query(
      collection(db, this.collectionName),
      where('cycleId', '==', cycleId),
      where('status', '!=', 'cancelled'),
      orderBy('status'),
      orderBy('updatedAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      objectivesQuery,
      (snapshot) => {
        console.log('[ObjectiveService] onSnapshot success', {
          docsCount: snapshot.docs.length,
          timestamp: new Date().toISOString()
        });
        const objectives = snapshot.docs.map((doc) =>
          this.formatObjective(doc.id, doc.data())
        );
        callback(objectives);
      },
      (error) => {
        console.error('[ObjectiveService] onSnapshot error', {
          error: error.message,
          code: error.code,
          cycleId,
          timestamp: new Date().toISOString()
        });
      }
    );

    return unsubscribe;
  }

  /**
   * Atualiza campos de hierarquia (path e ancestorIds) em cascata
   * @param objectiveId - ID do objetivo
   */
  async updateHierarchyFields(objectiveId: string): Promise<void> {
    const objectiveDoc = await getDoc(doc(db, this.collectionName, objectiveId));
    if (!objectiveDoc.exists()) {
      throw new Error('okr/objective-not-found: Objetivo não encontrado');
    }

    const objectiveData = objectiveDoc.data();
    const batch = writeBatch(db);

    // Buscar todos os descendentes
    const descendants = await this.getAllDescendants(objectiveId);

    // Atualizar cada descendente
    for (const descendant of descendants) {
      const newPath = this.calculatePath(descendant.parentObjectiveId || '', objectiveData);
      const newAncestorIds = this.calculateAncestorIds(
        descendant.parentObjectiveId || '',
        objectiveData
      );

      batch.update(doc(db, this.collectionName, descendant.id), {
        path: newPath,
        ancestorIds: newAncestorIds,
        updatedAt: serverTimestamp(),
        '_metadata.version': increment(1),
      });
    }

    await batch.commit();
  }

  /**
   * Calcula o path hierárquico baseado no pai
   * @private
   */
  private calculatePath(parentId: string, parentData?: unknown): string {
    if (!parentId || !parentData) {
      return '';
    }

    const parent = parentData as { path?: string; id?: string };
    return parent.path ? `${parent.path}/${parentId}` : parentId;
  }

  /**
   * Calcula os IDs dos ancestrais baseado no pai
   * @private
   */
  private calculateAncestorIds(parentId: string, parentData?: unknown): string[] {
    if (!parentId || !parentData) {
      return [];
    }

    const parent = parentData as { ancestorIds?: string[] };
    return parent.ancestorIds ? [...parent.ancestorIds, parentId] : [parentId];
  }

  /**
   * Calculates progress for an Objective based on its Key Results
   * Uses equal-weight averaging (arithmetic mean) of all KR progress values
   * Each KR contribution is capped at 100% to prevent over-achievement masking
   * @param objectiveId - ID of the Objective
   * @returns Calculated progress percentage (0-100)
   */
  async calculateProgress(objectiveId: string): Promise<number> {
    // Fetch all Key Results for this Objective
    const keyResults = await keyResultService.getByObjective(objectiveId);

    // Calculate aggregated progress using utility function
    return calculateObjectiveProgress(keyResults);
  }

  /**
   * Propagates progress from Key Results to parent Objective
   * Called automatically after a Key Result's progress changes
   * @param objectiveId - ID of the Objective to update
   */
  async propagateProgressFromKeyResults(objectiveId: string): Promise<void> {
    // Calculate new progress based on current KRs
    const newProgress = await this.calculateProgress(objectiveId);

    // Update Objective with calculated progress
    // Note: Using partial update to avoid triggering unnecessary validations
    await updateDoc(doc(db, this.collectionName, objectiveId), {
      progress: newProgress,
      updatedAt: serverTimestamp(),
      '_metadata.lastModifiedBy': 'system', // Auto-calculated field
    });
  }

  /**
   * Formata dados do Firestore para o tipo Objective
   * @private
   */
  private formatObjective(id: string, data: unknown): Objective {
    const obj = data as Record<string, unknown>;

    return {
      id,
      title: obj.title as string,
      description: (obj.description as string) || undefined,
      level: obj.level as 1 | 2 | 3 | 4,
      type: obj.type as 'committed' | 'aspirational',
      status: obj.status as 'draft' | 'active' | 'completed' | 'rolled' | 'cancelled',
      cycleId: obj.cycleId as string,
      ownerId: obj.ownerId as string,
      ownerName: obj.ownerName as string,
      ownerEmail: obj.ownerEmail as string,
      parentObjectiveId: (obj.parentObjectiveId as string) || undefined,
      parentObjectivePath: (obj.parentObjectivePath as string) || undefined,
      path: obj.path as string,
      ancestorIds: (obj.ancestorIds as string[]) || [],
      progress: (obj.progress as number) || 0,
      healthStatus: (obj.healthStatus as 'on_track' | 'at_risk' | 'off_track') || 'on_track',
      keyResultCount: (obj.keyResultCount as number) || 0,
      startDate: this.toTimestamp(obj.startDate),
      endDate: this.toTimestamp(obj.endDate),
      allowedRoles: obj.allowedRoles as string[],
      createdAt: this.toTimestamp(obj.createdAt),
      updatedAt: this.toTimestamp(obj.updatedAt),
      completedAt: obj.completedAt ? this.toTimestamp(obj.completedAt) : undefined,
      _metadata: {
        version: (obj._metadata as { version: number }).version || 1,
        lastModifiedBy: (obj._metadata as { lastModifiedBy: string }).lastModifiedBy || '',
      },
    } as Objective;
  }

  /**
   * Garante que o valor é um Timestamp do Firestore
   * @private
   */
  private toTimestamp(value: unknown): Timestamp {
    if (value instanceof Timestamp) {
      return value;
    }
    if (value instanceof Date) {
      return Timestamp.fromDate(value);
    }
    return Timestamp.now();
  }
}

export const objectiveService = new ObjectiveServiceClass();
